import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ServiceHealth, LogEntry, Alert, MetricTimeSeries } from '@/types/monitoring.types';

interface MonitoringSliceState {
  services: ServiceHealth[];
  logs: LogEntry[];
  alerts: Alert[];
  metrics: MetricTimeSeries[];
  overallHealth: 'healthy' | 'degraded' | 'critical';
  loading: boolean;
  error: string | null;
}

const initialState: MonitoringSliceState = {
  services: [],
  logs: [],
  alerts: [],
  metrics: [],
  overallHealth: 'healthy',
  loading: false,
  error: null,
};

const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setServices: (state, action: PayloadAction<ServiceHealth[]>) => {
      state.services = action.payload;
      // Calculate overall health
      const criticalCount = action.payload.filter(s => s.status === 'critical').length;
      const degradedCount = action.payload.filter(s => s.status === 'degraded').length;
      
      if (criticalCount > 0) {
        state.overallHealth = 'critical';
      } else if (degradedCount > 0) {
        state.overallHealth = 'degraded';
      } else {
        state.overallHealth = 'healthy';
      }
    },
    updateService: (state, action: PayloadAction<ServiceHealth>) => {
      const index = state.services.findIndex(s => s.name === action.payload.name);
      if (index !== -1) {
        state.services[index] = action.payload;
      } else {
        state.services.push(action.payload);
      }
    },
    setLogs: (state, action: PayloadAction<LogEntry[]>) => {
      state.logs = action.payload;
    },
    addLog: (state, action: PayloadAction<LogEntry>) => {
      state.logs.unshift(action.payload);
      if (state.logs.length > 1000) {
        state.logs.pop();
      }
    },
    setAlerts: (state, action: PayloadAction<Alert[]>) => {
      state.alerts = action.payload;
    },
    addAlert: (state, action: PayloadAction<Alert>) => {
      state.alerts.unshift(action.payload);
    },
    updateAlert: (state, action: PayloadAction<Alert>) => {
      const index = state.alerts.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        state.alerts[index] = action.payload;
      }
    },
    setMetrics: (state, action: PayloadAction<MetricTimeSeries[]>) => {
      state.metrics = action.payload;
    },
    clearLogs: (state) => {
      state.logs = [];
    },
  },
});

export const {
  setLoading,
  setError,
  setServices,
  updateService,
  setLogs,
  addLog,
  setAlerts,
  addAlert,
  updateAlert,
  setMetrics,
  clearLogs,
} = monitoringSlice.actions;

export default monitoringSlice.reducer;
